import {
  IFarmInputProduct,
  IFarmInputQuote,
  IFarmInputQuoteLineItem,
} from 'constants/interfaces';
import React, {useEffect, useState} from 'react';
import Autocomplete from '@material-ui/lab/Autocomplete';
import {
  Box,
  Button,
  Card,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Popper,
  TextField,
  Tooltip,
  Typography,
} from '@material-ui/core';
import EditIcon from '@material-ui/icons/Edit';
import CloseIcon from '@material-ui/icons/Close';
import CheckIcon from '@material-ui/icons/Check';
import TableContainer from '@material-ui/core/TableContainer';
import Table from '@material-ui/core/Table';
import TableHead from '@material-ui/core/TableHead';
import TableRow from '@material-ui/core/TableRow';
import TableCell from '@material-ui/core/TableCell';
import TableBody from '@material-ui/core/TableBody';
import {_NumberWithSpaces} from 'constants/index';
import AddIcon from '@material-ui/icons/Add';
import DeleteIcon from '@material-ui/icons/Delete';
import SaveIcon from '@material-ui/icons/Save';
import {Skeleton} from '@material-ui/lab';
import {DiscountService} from 'screens/Dashboard/Quotes/QuoteProducts/DiscountService';

export function QuoteProductInfo(props: {
  isLoading?: boolean;
  quote?: IFarmInputQuote;
  allInputProducts: IFarmInputProduct[];
  onLineItemUpdate: (lineItem: IFarmInputQuoteLineItem) => any;
  onLineItemAdd: (lineItem: IFarmInputQuoteLineItem) => any;
  onLineItemRemove: (lineItemId: number) => any;
  onSubmitQuote: () => any;
  isSubmittingQuote: boolean;
  isCreating?: boolean;
  readonly?: boolean;
  isCommodityGroup?: boolean;
}) {
  if (props.isLoading)
    return <Skeleton variant="rect" height={300} style={{borderRadius: 8}} />;
  if (!props.quote) return null;
  return (
    <QuoteProductsTable
      quote={props.quote}
      allInputProducts={props.allInputProducts}
      onEdit={(lineItem) => props.onLineItemUpdate(lineItem)}
      onAdd={(lineItem) => props.onLineItemAdd(lineItem)}
      onRemove={(lineItemId) => props.onLineItemRemove(lineItemId)}
      onSubmit={() => props.onSubmitQuote()}
      isSubmittingQuote={props.isSubmittingQuote}
      isCreating={props.isCreating}
      readonly={props.readonly}
      isCommodityGroup={props.isCommodityGroup}
    />
  );
}

function QuoteProductsTable(props: {
  quote: IFarmInputQuote;
  allInputProducts: IFarmInputProduct[];
  onEdit: (lineItem: IFarmInputQuoteLineItem) => any;
  onAdd: (lineItem: IFarmInputQuoteLineItem) => any;
  onRemove: (lineItemId: number) => any;
  onSubmit: () => any;
  isSubmittingQuote: boolean;
  isCreating?: boolean;
  readonly?: boolean;
  isCommodityGroup?: boolean;
}) {
  const [lineItems, setLineItems] = useState<IFarmInputQuoteLineItem[]>([]);
  const [isUnsaved, setIsUnsaved] = useState<boolean>(true);
  const [isAdding, setIsAdding] = useState<boolean>(false);
  const [isEditing, setIsEditing] = useState<boolean>(false);
  const [editIndex, setEditIndex] = useState<number>();
  const [editTotal, setEditTotal] = useState<number>();

  const [removeConfirmationIsOpen, setRemoveConfirmationIsOpen] =
    useState<boolean>(false);
  const [confirmRemoveIndex, setConfirmRemoveIndex] = useState(-1);

  // const lineItemInputIds = lineItems.map((item) => item.product.id);
  const availableInputProducts = props.allInputProducts
    // .filter((input) => !lineItemInputIds.includes(input.id))
    .sort((a, b) => a.name.localeCompare(b.name));
  const canEdit =
    !props.readonly &&
    props.quote.state !== 'REJECTED' &&
    props.quote.state !== 'ACCEPTED';
  const hasDiscountFields = !props.isCommodityGroup;

  useEffect(() => {
    window.onbeforeunload = () => {
      if (isUnsaved)
        return 'The information you’ve entered may not be saved. Are you sure you want to leave?';
    };
    return () => {
      window.onbeforeunload = () => undefined;
    };
  }, [isUnsaved]);

  useEffect(() => {
    lineItems?.forEach((lineItem) => props.onRemove(lineItem.id));
    const newLineItems = props.quote.lineItems.map((lineItem) => ({
      ...lineItem,
    }));
    setLineItems(newLineItems);
    newLineItems?.forEach((lineItem) => props.onAdd(lineItem));
  }, [props.quote.id]);

  const getTotal = () => {
    return lineItems.reduce(
      (sum: number, lineItem: IFarmInputQuoteLineItem, index: number) => {
        if (index === editIndex && editTotal) return sum + editTotal;
        const price = props.isCommodityGroup
          ? lineItem.product.price
          : lineItem.price;
        return sum + price * lineItem.quantity;
      },
      0,
    );
  };

  const handleEditLineItem = (lineItemIndex: number) => {
    setIsUnsaved(true);
    setIsEditing(true);
    setEditIndex(lineItemIndex);
    const lineItem = lineItems[lineItemIndex];
    setEditTotal(
      (props.isCommodityGroup ? lineItem.product.price : lineItem.price) *
        lineItem.quantity,
    );
  };

  const cancelEditLineItem = () => {
    setIsEditing(false);
    setEditIndex(undefined);
    setEditTotal(undefined);
  };

  const handleAddLineItem = () => {
    const defaultProduct = availableInputProducts[0];
    const addedLineItem: IFarmInputQuoteLineItem = {
      id: Math.max(0, ...lineItems.map((lineItem) => lineItem.id)) + 1,
      retailPrice: defaultProduct.price,
      price: defaultProduct.price,
      originalQuantity: 1,
      quantity: 1,
      product: defaultProduct,
    };

    setIsUnsaved(true);
    setIsEditing(true);
    setEditIndex(lineItems.length);
    setEditTotal(addedLineItem.price * addedLineItem.quantity);
    setLineItems([...lineItems, addedLineItem]);
    setIsAdding(true);
  };

  const cancelAddLineItem = () => {
    setIsEditing(false);
    setEditIndex(undefined);
    setEditTotal(undefined);
    setLineItems(lineItems.slice(0, -1));
    setIsAdding(false);
  };

  const submitLine = (
    editIndex: number,
    newProduct: IFarmInputProduct,
    newQty: number,
    newPrice: number,
    newCostPrice?: number,
  ) => {
    const updatedLineItems = cloneLineItems();
    const updatedLineItem = updatedLineItems[editIndex];
    updatedLineItem.product = newProduct;
    updatedLineItem.price = props.isCommodityGroup
      ? newProduct.price
      : newPrice;
    updatedLineItem.costPrice = props.isCommodityGroup
      ? undefined
      : newCostPrice;
    updatedLineItem.quantity = newQty;

    if (isAdding) props.onAdd(updatedLineItem);
    else props.onEdit(updatedLineItem);
    setLineItems(updatedLineItems);

    setIsUnsaved(false);
    cancelEditLineItem();
    if (isAdding) setIsAdding(false);
  };

  const onConfirmRemove = (lineItemIndex: number) => {
    setRemoveConfirmationIsOpen(true);
    setConfirmRemoveIndex(lineItemIndex);
  };

  const handleCancelRemove = () => {
    setRemoveConfirmationIsOpen(false);
    setConfirmRemoveIndex(-1);
  };

  const onRemove = () => {
    const lineItemId = lineItems[confirmRemoveIndex].id;
    const updatedLineItems = cloneLineItems().filter(
      (item, index) => index !== confirmRemoveIndex,
    );
    setLineItems(updatedLineItems);
    setIsUnsaved(true);
    props.onRemove(lineItemId);
    handleCancelRemove();
  };

  function cloneLineItems(): IFarmInputQuoteLineItem[] {
    const lineItemsClone = lineItems.map((lineItem) => ({...lineItem}));
    lineItemsClone.forEach((order) => (order.product = {...order.product}));
    return lineItemsClone;
  }

  return (
    <>
      {!props.isCreating && (
        <Header
          quote={props.quote}
          canEdit={canEdit}
          onSubmitQuote={props.onSubmit}
          isSubmittingQuote={props.isSubmittingQuote}
        />
      )}
      <Card variant="outlined" className="order-products-table-container-2">
        <TableContainer className="order-products-table">
          <Table
            className="order-products-table"
            aria-label="order products table"
            size="small"
          >
            <TableHead>
              <TableRow>
                <TableCell>Product</TableCell>
                <TableCell align="right">Retail&nbsp;(R)</TableCell>
                <TableCell align="right">Unit&nbsp;(R)</TableCell>
                {hasDiscountFields && (
                  <>
                    <TableCell align="right">Cost&nbsp;(R)</TableCell>
                    <TableCell align="right">Discount&nbsp;(R)</TableCell>
                    <TableCell align="right">Discount&nbsp;(%)</TableCell>
                  </>
                )}
                <TableCell align="right">Qty</TableCell>
                <TableCell align="right">Total&nbsp;(R)</TableCell>
                {/* icons*/}
                <TableCell align="right" />
              </TableRow>
            </TableHead>
            <TableBody>
              {lineItems.map(
                (lineItem: IFarmInputQuoteLineItem, index: number) =>
                  editIndex !== index ? (
                    <DisplayRow
                      key={index}
                      canUpdate={canEdit && !isEditing}
                      lineItem={lineItem}
                      isCommodityGroup={props.isCommodityGroup}
                      onEdit={() => handleEditLineItem(index)}
                      onRemove={() => onConfirmRemove(index)}
                    />
                  ) : (
                    <EditRow
                      key={index}
                      lineItem={lineItem}
                      canChangeProduct={isAdding}
                      productOptions={availableInputProducts}
                      onCancelEdit={
                        isAdding ? cancelAddLineItem : cancelEditLineItem
                      }
                      onSaveEdit={(product, qty, price, costPrice) =>
                        submitLine(index, product, qty, price, costPrice)
                      }
                      onTotalChange={setEditTotal}
                      isCommodityGroup={props.isCommodityGroup}
                    />
                  ),
              )}
              <TableRow>
                <TableCell />
                <TableCell />
                <TableCell />
                {hasDiscountFields && (
                  <>
                    <TableCell />
                    <TableCell />
                    <TableCell />
                  </>
                )}
                <TableCell colSpan={2} className="total" align="right">
                  {_NumberWithSpaces(getTotal())}
                </TableCell>
              </TableRow>
            </TableBody>
          </Table>
        </TableContainer>
        {canEdit && !isEditing && <Footer onAddLineItem={handleAddLineItem} />}
        <ConfirmRemoveProduct
          isOpen={removeConfirmationIsOpen}
          onCancel={handleCancelRemove}
          onConfirm={onRemove}
        />
      </Card>
    </>
  );
}

function Header(props: {
  quote: IFarmInputQuote;
  canEdit: boolean;
  onSubmitQuote: () => any;
  isSubmittingQuote: boolean;
}) {
  const {quote, canEdit, isSubmittingQuote, onSubmitQuote} = props;
  return (
    <Box className="order-products-table-container-2 header-row">
      <Chip label={quote.formattedState} variant="outlined" />
      {canEdit && !isSubmittingQuote && (
        <Button
          variant="outlined"
          startIcon={<SaveIcon />}
          onClick={onSubmitQuote}
          size="small"
          color={'primary'}
        >
          Submit Quote
        </Button>
      )}
      {isSubmittingQuote && <CircularProgress size={20} />}
    </Box>
  );
}

function Footer(props: {onAddLineItem: () => any}) {
  return (
    <Box
      className="order-product-row"
      m={1}
      display="flex"
      justifyContent="center"
    >
      <Button
        size="small"
        className="primary-button"
        startIcon={<AddIcon />}
        onClick={props.onAddLineItem}
        color={'primary'}
      >
        Add Line Item
      </Button>
    </Box>
  );
}

function DisplayRow(props: {
  lineItem: IFarmInputQuoteLineItem;
  canUpdate: boolean;
  isCommodityGroup?: boolean;
  onEdit: () => any;
  onRemove: () => any;
}) {
  const {lineItem, canUpdate} = props;
  const price = props.isCommodityGroup
    ? lineItem.product.price
    : lineItem.price;

  const discountCalculation = new DiscountService(
    Number(lineItem.product.price),
  ).fromDiscountedPrice(Number(price));

  return (
    <TableRow className="order-product-row" key={lineItem.id}>
      <TableCell className="order-product-name">
        <Typography variant="body2" color="textSecondary">
          {lineItem.product.name}{' '}
          {lineItem.product.activeIngredient
            ? '(' + lineItem.product.activeIngredient + ')'
            : ''}{' '}
          - {lineItem.product.brand?.name}
        </Typography>
      </TableCell>
      <TableCell className="order-product-price" align="right">
        <Typography variant="body2" color="textSecondary">
          {_NumberWithSpaces(lineItem.product.price)}
        </Typography>
      </TableCell>
      <TableCell className="order-product-price" align="right">
        <Typography variant="body2" color="textSecondary">
          {_NumberWithSpaces(price)}
        </Typography>
      </TableCell>
      {!props.isCommodityGroup && (
        <>
          <TableCell className="order-product-price" align="right">
            <Typography variant="body2" color="textSecondary">
              {lineItem.costPrice ? _NumberWithSpaces(lineItem.costPrice) : ''}
            </Typography>
          </TableCell>
          <TableCell className="order-product-price" align="right">
            <Typography variant="body2" color="textSecondary">
              {_NumberWithSpaces(discountCalculation.discountAmount.toFixed(2))}
            </Typography>
          </TableCell>
          <TableCell className="order-product-qty" align="right">
            <Typography variant="body2" color="textSecondary">
              {discountCalculation.discountPercentage.toFixed(2)}
            </Typography>
          </TableCell>
        </>
      )}
      <TableCell className="order-product-qty" align="right">
        <Typography variant="body2" color="textSecondary">
          {lineItem.quantity}
        </Typography>
      </TableCell>
      <TableCell align="right">
        <Typography variant="body2" color="textSecondary">
          {_NumberWithSpaces(price * lineItem.quantity)}
        </Typography>
      </TableCell>
      <TableCell align="right">
        {canUpdate && (
          <>
            <Tooltip title="Edit" aria-label="edit">
              <IconButton aria-label="edit" size="small" onClick={props.onEdit}>
                <EditIcon fontSize="inherit" />
              </IconButton>
            </Tooltip>
            <Tooltip title="Remove" aria-label="remove">
              <IconButton
                aria-label="remove"
                size="small"
                onClick={props.onRemove}
              >
                <DeleteIcon fontSize="inherit" />
              </IconButton>
            </Tooltip>
          </>
        )}
      </TableCell>
    </TableRow>
  );
}

function EditRow(props: {
  lineItem: IFarmInputQuoteLineItem;
  canChangeProduct: boolean;
  productOptions: IFarmInputProduct[];
  onCancelEdit: () => any;
  onSaveEdit: (
    product: IFarmInputProduct,
    qty: number,
    price: number,
    costPrice?: number,
  ) => any;
  onTotalChange: (total: number) => any;
  isCommodityGroup?: boolean;
}) {
  const {lineItem, canChangeProduct, productOptions, onTotalChange} = props;
  const [newProduct, setNewProduct] = useState<IFarmInputProduct>(
    lineItem.product,
  );
  const [newQty, setNewQty] = useState<string>(lineItem.quantity.toString());
  const [newPrice, setNewPrice] = useState<string>(lineItem.price.toString());
  const [newCostPrice, setNewCostPrice] = useState<string>(
    lineItem.costPrice?.toString() || '',
  );
  const [discountAmount, setDiscountAmount] = useState<string>('0');
  const [discountPercentage, setDiscountPercentage] = useState<string>('0');

  const isNumber = (value?: string) => !isNaN(Number(value));

  const linePrice = props.isCommodityGroup
    ? newProduct.price.toString()
    : newPrice;
  const lineTotal = parseFloat(linePrice!) * parseFloat(newQty!);
  const canSave =
    !!newQty &&
    isNumber(newQty) &&
    Number(newQty) > 0 &&
    !!linePrice &&
    isNumber(linePrice) &&
    Number(linePrice) > 0;

  const handleProductUpdate = (product: IFarmInputProduct) => {
    setNewProduct(product);
    setNewQty('1');
    setNewPrice(product.price.toString());
    setDiscountAmount('0');
    setDiscountPercentage('0');
  };

  const handleSaveEdit = () => {
    const qty = parseFloat(newQty);
    const price = props.isCommodityGroup
      ? newProduct.price
      : parseFloat(parseFloat(newPrice).toFixed(2));
    const costPrice = props.isCommodityGroup
      ? undefined
      : parseFloat(parseFloat(newCostPrice).toFixed(2)) || undefined;
    props.onSaveEdit(newProduct, qty, price, costPrice);
  };

  useEffect(() => {
    onTotalChange(lineTotal || 0);
  }, [newQty, newPrice, lineTotal, onTotalChange]);

  const rounded = (value: string | number) =>
    Number(value).toFixed(2).toString();

  const handlePriceChange = (price: string) => {
    setNewPrice(price);

    if (!isNumber(price)) {
      setDiscountAmount('0');
      setDiscountPercentage('0');
      return;
    }

    const discountCalculation = new DiscountService(
      newProduct.price,
    ).fromDiscountedPrice(Number(price));

    setDiscountAmount(rounded(discountCalculation.discountAmount));
    setDiscountPercentage(rounded(discountCalculation.discountPercentage));
  };

  const handleDiscountPercentageChange = (discountPercentage: string) => {
    setDiscountPercentage(discountPercentage);

    if (!isNumber(discountPercentage)) {
      setNewPrice(rounded(newProduct.price));
      setDiscountAmount('0');
      return;
    }

    const discountCalculation = new DiscountService(
      newProduct.price,
    ).fromDiscountPercentage(Number(discountPercentage));

    setNewPrice(rounded(discountCalculation.discountedPrice));
    setDiscountAmount(rounded(discountCalculation.discountAmount));
  };

  const handleDiscountAmountChange = (discountAmount: string) => {
    setDiscountAmount(discountAmount);
    if (!isNumber(discountAmount)) {
      setNewPrice(rounded(newProduct.price));
      setDiscountPercentage('0');
      return;
    }

    const discountCalculation = new DiscountService(
      newProduct.price,
    ).fromDiscountAmount(Number(discountAmount));

    setNewPrice(rounded(discountCalculation.discountedPrice));
    setDiscountPercentage(rounded(discountCalculation.discountPercentage));
  };

  return (
    <TableRow className="order-product-row" key={lineItem.id}>
      <TableCell className="order-product-name">
        {canChangeProduct ? (
          <Autocomplete
            id="inputs"
            disableClearable
            options={productOptions}
            getOptionLabel={(product: IFarmInputProduct) =>
              `${product.name} ${
                product.activeIngredient
                  ? '(' + product.activeIngredient + ')'
                  : ''
              } - ${product.brand?.name || ''}`
            }
            getOptionSelected={(option) => option.id === newProduct?.id}
            size="small"
            value={newProduct}
            onChange={(event, value) => handleProductUpdate(value)}
            renderInput={(params) => (
              <TextField {...params} size="small" variant="outlined" />
            )}
            PopperComponent={(props) => (
              <Popper {...props} style={{width: 'fit-content'}} />
            )}
          />
        ) : (
          <Typography
            className="order-product-name"
            variant="body2"
            color="textSecondary"
          >
            {lineItem.product.name}
          </Typography>
        )}
      </TableCell>
      <TableCell className="order-product-price" align="right">
        <Typography variant="body2" color="textSecondary">
          {_NumberWithSpaces(newProduct.price)}
        </Typography>
      </TableCell>
      <TableCell className="order-product-price" align="right">
        {props.isCommodityGroup ? (
          <Typography variant="body2" color="textSecondary">
            {_NumberWithSpaces(newProduct.price)}
          </Typography>
        ) : (
          <TextField
            size="small"
            value={newPrice}
            type="text"
            InputProps={{inputProps: {min: 1}}}
            variant="outlined"
            onChange={(event) => handlePriceChange(event.target.value)}
          />
        )}
      </TableCell>
      {!props.isCommodityGroup && (
        <>
          <TableCell className="order-product-price" align="right">
            <TextField
              size="small"
              value={newCostPrice}
              type="text"
              InputProps={{inputProps: {min: 1}}}
              variant="outlined"
              onChange={(event) => setNewCostPrice(event.target.value)}
            />
          </TableCell>
          <TableCell className="order-product-price" align="right">
            <TextField
              size="small"
              value={discountAmount}
              type="text"
              InputProps={{inputProps: {min: 1}}}
              variant="outlined"
              onChange={(event) =>
                handleDiscountAmountChange(event.target.value)
              }
            />
          </TableCell>
          <TableCell className="order-product-qty" align="right">
            <TextField
              size="small"
              value={discountPercentage}
              type="number"
              InputProps={{inputProps: {min: 0, step: 1}}}
              variant="outlined"
              onChange={(event) =>
                handleDiscountPercentageChange(event.target.value)
              }
            />
          </TableCell>
        </>
      )}
      <TableCell className="order-product-qty" align="right">
        <TextField
          size="small"
          value={newQty}
          type="number"
          InputProps={{inputProps: {min: 0, step: 1}}}
          variant="outlined"
          onChange={(event) => setNewQty(event.target.value)}
        />
      </TableCell>
      <TableCell align="right">
        <Typography variant="body2" color="textSecondary">
          {canSave ? _NumberWithSpaces(lineTotal) : null}
        </Typography>
      </TableCell>
      <TableCell align="right">
        <div className="row">
          <Tooltip title="Save" aria-label="save">
            <IconButton
              aria-label="save"
              size="small"
              onClick={handleSaveEdit}
              disabled={!canSave}
            >
              <CheckIcon fontSize="inherit" />
            </IconButton>
          </Tooltip>
          <Tooltip title="Cancel" aria-label="cancel">
            <IconButton
              aria-label="cancel"
              size="small"
              onClick={props.onCancelEdit}
            >
              <CloseIcon fontSize="inherit" />
            </IconButton>
          </Tooltip>
        </div>
      </TableCell>
    </TableRow>
  );
}

function ConfirmRemoveProduct(props: {
  isOpen: boolean;
  onCancel: () => any;
  onConfirm: () => any;
}) {
  const {isOpen, onCancel, onConfirm} = props;
  return (
    <Dialog
      className="quote-line-item-remove-confirmation-dialog"
      disableEscapeKeyDown
      open={isOpen}
      onClose={onCancel}
    >
      <DialogTitle className="heading">Remove Line Item</DialogTitle>
      <DialogContent className="body">
        Are you sure you want to remove the line item from the quote?
      </DialogContent>
      <DialogActions>
        <Button className="cancel-button" onClick={onCancel}>
          Cancel
        </Button>
        <Button className="accept-button" onClick={onConfirm}>
          Remove
        </Button>
      </DialogActions>
    </Dialog>
  );
}
